/**
 * Technology Processing Utilities
 *
 * Pure functions for processing technology tree data and combining with technique attributes.
 * Implements immutable patterns for safe data transformation without side effects.
 */

import { TechnologyAttributeUnion } from '@/sections/asset/components/tabs';
import { Attribute, Port, Technology, TreeNode } from '@/types';

/**
 * Result of extracting technology data from a tree node
 */
interface ExtractedTechnologyData {
  technology?: Technology; // Optional - some ports don't have detected technologies
  port: Port;
}

/**
 * Extracts technology and port from a single tree node
 *
 * @param technologyTree - Tree node containing technology relationship data
 * @returns Extracted technology data or null if port is missing (technology is optional)
 */
function extractTechnologyFromTreeNode(
  technologyTree: TreeNode
): ExtractedTechnologyData | null {
  const portTarget = technologyTree?.node?.relationships?.[0]?.target;

  if (!portTarget?.result?.ports?.[0]) {
    return null;
  }

  const port = portTarget.result.ports[0] as Port;
  const technology = portTarget.relationships?.[0]?.target?.result
    ?.technologies?.[0] as Technology | undefined;

  return { technology, port };
}

/**
 * Creates a technology-port union from extracted data
 *
 * @param data - Extracted technology and port data
 * @returns Technology-port union for display
 */
function createTechnologyAttributeUnion(
  data: ExtractedTechnologyData
): TechnologyAttributeUnion {
  return {
    port: data.port,
    technology: data.technology,
    visited: data.port.visited,
  };
}

/**
 * Processes technologies tree to extract technology-port unions
 *
 * @param technologiesTree - Tree of technology relationships from graph query
 * @returns Array of technology-port unions and set of processed port keys
 */
function processTechnologyTreeNodes(technologiesTree: TreeNode[]): {
  technologies: TechnologyAttributeUnion[];
  processedPortKeys: Set<string>;
} {
  const technologies: TechnologyAttributeUnion[] = [];
  const processedPortKeys = new Set<string>();

  for (const technologyTree of technologiesTree) {
    const extracted = extractTechnologyFromTreeNode(technologyTree);

    if (extracted) {
      technologies.push(createTechnologyAttributeUnion(extracted));
      processedPortKeys.add(extracted.port.key);
    }
  }

  return { technologies, processedPortKeys };
}

/**
 * Main entry point: Processes technologies tree to extract port-technology unions
 *
 * This function performs the following operations:
 * 1. Extracts technology-port pairs from the technology tree
 * 2. Returns all ports with their associated technologies
 *
 * The result is used to display technology information in the asset drawer,
 * showing ports with their detected technologies.
 *
 * @param technologiesTree - Tree of technology relationships from graph query
 * @param techniqueAttributes - Unused (kept for backwards compatibility with caller)
 * @returns Array of technology-port unions for display
 */
export function processTechnologies(
  technologiesTree: TreeNode[],
  techniqueAttributes: Record<string, Attribute>
): TechnologyAttributeUnion[] {
  // Extract technologies from tree
  const { technologies } = processTechnologyTreeNodes(technologiesTree);

  return technologies;
}
